import {
  useGetAllCreatives,
  useGetCreativeLineItemTrigger,
  usePatchCreative,
  usePatchCreativeLineItem,
} from '@apis/creatives';
import { GROUP_TYPE, useGroups } from '@apis/groups';
import { pick } from 'lodash';
import { removeEmptyValues } from '../../utils';
import { bwSyncParams } from '@components/containers/WizardPage/constants';
import { fields } from '@components/CampaignWizard/AdGroupSettingsSection/formConfig';
import { isWeightedDelivery } from '../utils/creatives';
import type { Campaign } from '@local-types/campaign';
import type { UseFormReturn } from 'react-hook-form';
import type { Fields } from '@local-types/general';
import { AdGroup } from '@local-types/group';
import { useGroupSectionPrefetch } from './useGroupSectionPrefetch';

export const useGroupSectionSubmitAdGroup = ({
  disabled,
  campaign,
  form,
  onAfterSubmit,
  shouldShowOutOfSyncSubmitPrompt,
  adGroup,
}: {
  disabled: boolean;
  campaign: Campaign;
  shouldShowOutOfSyncSubmitPrompt?: boolean;
  onAfterSubmit: () => void;
  form: UseFormReturn<Fields<typeof fields>>;
  adGroup: AdGroup;
}) => {
  const { prepareRequestData } = useGroupSectionPrefetch({
    adGroup,
    campaign,
    form,
    isDisplay: false,
  });

  const {
    trigger: updateLineItemCreative,
    isMutating: isCreativePostMutating,
  } = usePatchCreativeLineItem();

  const { invalidate: invalidateCreatives } = useGetAllCreatives({
    disabled,
  });

  const {
    update: updateAdGroup,
    create: createAdGroup,
    isLoading: isAdGroupProcessing,
  } = useGroups(GROUP_TYPE.AD_GROUP, campaign?.id);

  const { mutate: invalidateAdGroupSync } = useGroups(
    GROUP_TYPE.AD_GROUP,
    campaign?.id,
    {
      ...(shouldShowOutOfSyncSubmitPrompt && { params: bwSyncParams }),
    }
  );

  const { trigger: updateCreative } = usePatchCreative();
  const { trigger: getCreativeLineItem } = useGetCreativeLineItemTrigger();

  const handleSubmit = async (
    options: {
      updateAdGroupParams?: Record<string, string>;
    } = {}
  ) => {
    const values = form.getValues();
    const { updateAdGroupParams } = options;
    const { creativesAdditionalData, ...rest } = prepareRequestData();

    const res = await (!adGroup?.temporary
      ? updateAdGroup({
          id: adGroup.id,
          params: updateAdGroupParams,
          ...rest,
        })
      : createAdGroup(rest));

    /** TODO: move creatives hooks to typescript. */
    /* @ts-expect-error js file doesn't recognize trigger params. */
    const lineItems = (await getCreativeLineItem(res.id)) as AdGroup[];
    const isCreativesDirty = form.formState.dirtyFields.creatives;

    if (isCreativesDirty) {
      await Promise.all(
        (creativesAdditionalData ?? []).map(
          async ({
            id,
            weighting,
            name,
            click_url,
            pixels,
            start_date,
            end_date,
          }) => {
            const updatedData = pick(
              lineItems.find(({ creative }) => creative === id),
              ['id', 'lineitem', 'creative', 'weighting']
            );

            await updateCreative({
              ...removeEmptyValues({
                name,
                pixels,
                click_url,
              }),
              id: updatedData.creative,
            });
            await updateLineItemCreative({
              id: updatedData.id,
              creative: updatedData.creative,
              lineitem: updatedData.lineitem,
              // only update weighting if it's weighted delivery and is different
              ...(weighting !== updatedData?.weighting &&
                isWeightedDelivery(values) && { weighting }),
              start_date,
              end_date,
            });
          }
        )
      );

      invalidateCreatives();
    }
    if (shouldShowOutOfSyncSubmitPrompt) {
      invalidateAdGroupSync();
    }

    onAfterSubmit();
  };

  return {
    submit: handleSubmit,
    mutating: isCreativePostMutating || isAdGroupProcessing,
  };
};
